import { useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from '@ets/design-system';

import { useSelector, useSuggestions } from '_common/hooks';
import { useVirtualizedList } from '../VirtualizedList/VirtualizedListContext';

import { InteractionController } from '_common/components';
import { SuiteAvatar } from '_common/suite/components';
import { isRichTextEmpty, Textarea, TextareaProps, type TextareaAPI } from '_common/lib/components';
import { InteractionControllerProps } from '_common/components/OnboardingOverlay/InteractionController';

import ReplyCard, { type ReplyCardProps } from './ReplyCard/ReplyCard';

import styles from './ReplyList.module.scss';

export type ReplyListProps = {
  replies: Card.Reply[];
  commentId: PDF.Annotation['id'];
  collaborators: UserId[];
  canComment: boolean;
  newReplyInteraction: Omit<InteractionControllerProps, 'children'>;
  createReply: (reply: RichTextJSON) => void;
  editReply: (payload: { replyId: string; newContent: RichTextJSON }) => void;
  /** ID to be used in unit or automated tests.
   * Will result in 4 different attributes:
   * * ${testId}-card-${index}-root
   * * ${testId}-card-${index}-editable
   * * ${testId}-card-${index}-view
   * * ${testId}-new-reply-container
   */
  testId: string;
} & Pick<
  ReplyCardProps,
  | 'voteReply'
  | 'deleteReply'
  | 'canEditReply'
  | 'canDeleteReply'
  | 'canVoteReply'
  | 'optionsToggleTooltip'
  | 'likeToggleTooltip'
  | 'legacyModal'
>;

const ReplyList = ({
  replies,
  commentId,
  collaborators,
  canComment,
  newReplyInteraction,
  optionsToggleTooltip,
  likeToggleTooltip,
  createReply,
  editReply,
  deleteReply,
  voteReply,
  canEditReply,
  canDeleteReply,
  canVoteReply,
  legacyModal,
  testId,
}: ReplyListProps) => {
  const intl = useIntl();
  const { openReplies, setReplyState, virtualized } = useVirtualizedList();
  const suggestions = useSuggestions(collaborators);

  const currentUserId = useSelector((state) => state.auth.userId);

  const replyRef = useRef<TextareaAPI>(null);
  const [reply, setReply] = useState<RichTextJSON>();

  const [localShowFooter, setLocalShowFooter] = useState(false);
  const showFooter = useMemo(
    () => (virtualized ? openReplies[commentId] === 'footer' : localShowFooter),
    [virtualized, openReplies, commentId, localShowFooter],
  );

  const mutableReplies = useMemo(() => [...replies].reverse(), [replies]);

  const handleCancelReply = () => {
    replyRef.current?.clear();

    if (virtualized) {
      setReplyState(commentId, 'open');
    } else {
      setLocalShowFooter(false);
    }
  };

  const handleCreateReply = () => {
    if (!reply) {
      return;
    }

    createReply(reply);
    replyRef.current?.clear();

    if (virtualized) {
      setReplyState(commentId, 'open');
    } else {
      setLocalShowFooter(false);
    }
  };

  const handleFocus = () => {
    if (virtualized) {
      setReplyState(commentId, 'footer');
    } else {
      setLocalShowFooter(true);
    }
  };

  const handleReplyChange: TextareaProps['onChange'] = (e) => {
    const target = e.target as HTMLTextAreaElement;
    setReply(target.value as unknown as RichTextJSON);
  };

  return (
    <div className={styles.root}>
      {mutableReplies.length > 0 && (
        <InteractionController style={{ display: 'block' }} {...newReplyInteraction}>
          <div className={styles.list}>
            {mutableReplies.map((reply, index) => (
              <ReplyCard
                key={`replycard-${reply.id}-of-${commentId}`}
                reply={reply}
                collaborators={collaborators}
                testId={`${testId}-card-${index}`}
                optionsToggleTooltip={optionsToggleTooltip}
                likeToggleTooltip={likeToggleTooltip}
                editReply={editReply}
                deleteReply={deleteReply}
                voteReply={voteReply}
                canEditReply={canEditReply}
                canDeleteReply={canDeleteReply}
                canVoteReply={canVoteReply}
                legacyModal={legacyModal}
              />
            ))}
          </div>
        </InteractionController>
      )}
      {canComment && (
        <InteractionController style={{ display: 'block' }} {...newReplyInteraction}>
          <div className={styles.newReply} data-testid={`${testId}-new-reply-container`}>
            <div className={styles.body}>
              <SuiteAvatar userId={currentUserId} />
              <Textarea
                size="m"
                name="reply"
                type="json"
                variant="compact"
                ref={replyRef}
                placeholder={intl.formatMessage({ id: 'REPLY_PLACEHOLDER' })}
                onChange={handleReplyChange}
                onFocus={handleFocus}
                onClick={(e) => e.stopPropagation()}
                mentions={suggestions}
                noMentionsLabel={intl.formatMessage({ id: 'NO_USERS_AVAILABLE' })}
                testId={`${testId}-rich-text-editor`}
                shouldFocus
                noColor
              />
            </div>
            {showFooter && (
              <div className={styles.footer}>
                <Button variant="link" size="s" onClick={handleCancelReply} testId="cancel">
                  <FormattedMessage id="global.cancel" />
                </Button>
                <Button
                  variant="primary"
                  size="s"
                  onClick={handleCreateReply}
                  disabled={isRichTextEmpty(reply)}
                  testId="reply"
                >
                  <FormattedMessage id="editor.comments.reply" />
                </Button>
              </div>
            )}
          </div>
        </InteractionController>
      )}
    </div>
  );
};

export default ReplyList;
